import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, CircularProgress, Grid, Toolbar } from '@material-ui/core'
import clsx from 'clsx'

import { useSelector } from 'react-redux'
import * as tourActions from 'actions/tours'
import * as uiActions from 'actions/ui'
import * as shipmentsAction from 'actions/shipments'

import * as fromCombined from 'selectors/combined'
import * as fromUi from 'selectors/ui'
import * as fromWarehouse from 'selectors/warehouse'

import { ShipmentTour } from 'common/models/typings/Tour'

import Typography from 'common/components/base/Typography'
import Button from 'common/components/base/Button'
import Icon from 'common/components/base/Icon'

import NoTours from 'common/components/features/EmptyState/NoTours'

import NotificationTour from 'common/components/features/TourPlanning/Notifications/Tour'
import AnimatedTourCard from 'common/components/features/TourCard'
import TourMap from 'common/components/features/TourMap'
import NotificationMap from 'common/components/features/TourMap/Notification'
import CreateToursDialog from 'common/components/features/CreateToursDialog'
import CreateToursDialogResult from 'common/components/features/CreateToursDialog/Results'
import DialogOverview from 'common/components/features/TourPlanning/Dialog/Overview'

import useStyles from './useStyles'
import { NETWORK_TYPE } from '../../../common/models/typings/TourOverview'
import * as fromNetworkType from '../../../selectors/networkType'
import NetworkNotReady from '../../../common/components/features/EmptyState/NetworkNotReady'
import { capitalize } from '../../../utils/capitalize'
import { useAppDispatch } from 'planner-upgrades/redux/hooks'

const TourPlanningPage = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const classes = useStyles()

  const networkType: NETWORK_TYPE = useSelector(fromNetworkType.getNetworkType)
  const currentNetworkReady = useSelector(fromWarehouse.getCurrentNetworkReady)

  const optimizeResult = useSelector((state: any) => state.ui.optimizeResult)
  const tours: ShipmentTour[] = useSelector(fromCombined.getShipmentTours)
  const locked: boolean = useSelector(fromCombined.areToursLocked)
  const selectedMarkers: ShipmentTour['id'][] = useSelector(
    fromUi.getSelectedCollations,
  )

  const [createDialog, setCreateDialog] = React.useState(false)
  const [overviewDialog, setOverviewDialog] = React.useState(false)
  const [creationCounter, setCreationCounter] = React.useState(0)
  const [showPolylines, setShowPolylines] = React.useState(true)
  const [loading, setLoading] = React.useState(false)
  const [saveLoading, setSaveLoading] = React.useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(true)
  const [isFullscreen, setIsFullscreen] = React.useState(false)

  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (hasUnsavedChanges) {
      event.preventDefault()
      // eslint-disable-next-line no-param-reassign
      event.returnValue = t('UNSAVED_CHANGES_WARNING')
    }
  }

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [hasUnsavedChanges])

  const handleOpenCreateDialog = () => {
    setCreateDialog(true)
  }

  const handleCloseCreateDialog = () => {
    setCreateDialog(false)
  }

  const handleOpenOverviewDialog = () => {
    setOverviewDialog(true)
  }

  const handleCloseOverviewDialog = () => {
    setOverviewDialog(false)
  }

  const handleOptimizationReset = React.useCallback(() => {
    dispatch(uiActions.setOptimizeResponse(undefined))
  }, [dispatch])

  const handleOptimize = React.useCallback(async () => {
    // reload shipments to make sure they are included in optimization
    setLoading(true)
    await dispatch(shipmentsAction.reloadShipments())

    // reset the optimization result
    handleOptimizationReset()

    // reopen the creating dialog loader
    setCreateDialog(true)

    // optimize
    await dispatch(shipmentsAction.assignToTours())

    // increment counter
    setCreationCounter((prev) => prev + 1)

    // close dialog
    setCreateDialog(false)
    setLoading(false)
    setHasUnsavedChanges(true)
  }, [dispatch, handleOptimizationReset])

  const handleSaveTour = React.useCallback(
    async (isLocked?: boolean) => {
      setSaveLoading(true)
      await dispatch(tourActions.saveTours(isLocked, networkType))
      await dispatch(tourActions.saveDrivers())

      // remove expanded tour card
      dispatch(tourActions.setSelectedTourId(null))
      // remove hidden tours
      dispatch(tourActions.setHiddenTourIds(0, true))
      setSaveLoading(false)
      setHasUnsavedChanges(false)
    },
    [dispatch, networkType],
  )

  return (
    <>
      {tours.length < 1 || !currentNetworkReady ? (
        <Box mt={10}>
          {!currentNetworkReady ? (
            <NetworkNotReady />
          ) : (
            <NoTours onPrimaryAction={handleOpenCreateDialog} />
          )}
        </Box>
      ) : (
        <>
          {!isFullscreen && (
            <div className="w-full flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => handleSaveTour()}
                  disabled={locked || saveLoading}
                  startIcon={saveLoading && <CircularProgress size={20} />}
                >
                  <Typography variant="button" transform="capitalize">
                    {t('SAVE_PLANNING_BY_NETWORK', {
                      networkType: capitalize(t(`NETWORK_TYPE.${networkType}`)),
                    })}
                  </Typography>
                </Button>
                <Button
                  color="white"
                  startIcon={<Icon name="plus" fill="currentColor" />}
                  onClick={handleOpenCreateDialog}
                  disabled={locked}
                >
                  <Typography variant="button" transform="capitalize">
                    {t('CREATE_NEW_TOURS_BY_NETWORK', {
                      networkType: capitalize(t(`NETWORK_TYPE.${networkType}`)),
                    })}
                  </Typography>
                </Button>
              </div>
              <div className="w-full flex justify-end ">
                <Button
                  variant="flat"
                  color="background"
                  startIcon={<Icon name="table" />}
                  onClick={handleOpenOverviewDialog}
                >
                  <Typography variant="button" transform="capitalize">
                    {t('OVERVIEW')}
                  </Typography>
                </Button>
              </div>
            </div>
          )}
          <div
            className={clsx('flex flex-row w-full pt-4 mb-[-32px]', {
              'fixed top-0 left-0 right-0 bottom-0 z-[1300] bg-white m-0 w-full h-dvh px-4':
                isFullscreen,
              'h-[calc(100vh-180px)]': !isFullscreen,
            })}
          >
            <div className="h-full w-2/5 pr-4 overflow-y-auto flex flex-col">
              {!isFullscreen && <NotificationTour />}
              <AnimatedTourCard />
            </div>
            <div className={clsx('w-3/5 pl-4')}>
              <div
                className={clsx('relative', {
                  'h-[100vh]': isFullscreen,
                  'h-full': !isFullscreen,
                })}
              >
                <NotificationMap />
                <TourMap
                  fullscreen={isFullscreen}
                  showPolylines={showPolylines}
                />
                <div className="absolute top-2 right-2 z-[1000]">
                  <div className="flex items-center gap-2">
                    <Button
                      className={classes.showPolylineButton}
                      color="white"
                      onClick={() => setShowPolylines((prev) => !prev)}
                      startIcon={
                        showPolylines ? (
                          <Icon name="hide" />
                        ) : (
                          <Icon name="view" />
                        )
                      }
                    >
                      <Typography variant="button" transform="capitalize">
                        {showPolylines
                          ? t('HIDE_POLYLINES')
                          : t('SHOW_POLYLINES')}
                      </Typography>
                    </Button>
                    <Button
                      color="white"
                      onClick={() => setIsFullscreen((prev) => !prev)}
                      startIcon={
                        isFullscreen ? (
                          <Icon name="minimize" />
                        ) : (
                          <Icon name="maximize" />
                        )
                      }
                    >
                      <Typography variant="button" transform="capitalize">
                        {isFullscreen ? t('EXIT_FULLSCREEN') : t('FULLSCREEN')}
                      </Typography>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      <CreateToursDialog
        open={createDialog}
        onClose={handleCloseCreateDialog}
        onOptimize={handleOptimize}
        loading={loading}
      />
      <CreateToursDialogResult
        open={Boolean(optimizeResult?.visible)}
        onClose={handleOptimizationReset}
        onReOptimize={handleOptimize}
      />
      <DialogOverview
        open={overviewDialog}
        onClose={handleCloseOverviewDialog}
      />
    </>
  )
}

export default TourPlanningPage
